import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './auth-context';

export interface UseLogoutOptions {
  /** Dokad przekierowac po wylogowaniu. Domyslnie ekran logowania. */
  redirectTo?: string;
}

export interface UseLogoutResult {
  /** Wylogowuje uzytkownika. Bezpieczne przy wielokrotnym wywolaniu. */
  logout: () => Promise<void>;
  isLoggingOut: boolean;
}

/**
 * Kompletne wylogowanie: unieważnienie sesji na serwerze, wyczyszczenie tokenów,
 * usunięcie pamięci podręcznej zapytań i przekierowanie.
 *
 * Czyszczenie cache React Query jest tu istotne — bez tego dane poprzedniego
 * uzytkownika zostalyby w pamieci i mignelyby kolejnej osobie logujacej sie
 * na tym samym urzadzeniu.
 */
export function useLogout({ redirectTo = '/logowanie' }: UseLogoutOptions = {}): UseLogoutResult {
  const { logout: clearAuthSession } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const logout = useCallback(async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);

    try {
      // `clearAuthSession` nie rzuca — nieudane wylogowanie po stronie serwera
      // i tak konczy sie usunieciem tokenow lokalnie.
      await clearAuthSession();
      queryClient.clear();
      await navigate(redirectTo, { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  }, [clearAuthSession, isLoggingOut, navigate, queryClient, redirectTo]);

  return { logout, isLoggingOut };
}
